import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowRight, LogOut } from 'lucide-react';
import Button from './ui/Button';
import { useAuthStore } from '../store/authStore';

/**
 * Hard paywall shown after the 7-day Pro trial expires.
 * Mounted globally in Layout.tsx so it covers every protected route.
 *
 * Behaviour:
 *  - Not closeable: no backdrop dismiss, no Escape-to-close, no X button.
 *    Escape key triggers logout instead of closing (safe escape hatch).
 *  - Focus trap: keeps tab focus inside the modal.
 *  - Aria: role=alertdialog with aria-modal/aria-labelledby/aria-describedby.
 *  - Two CTAs: subscribe (-> /pricing) or logout.
 *
 * Detection is derived from `user.subscription` so the same data that
 * already powers TrialBanner drives the modal — no extra API call.
 */
export default function TrialExpiredModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const containerRef = useRef<HTMLDivElement>(null);

  const subscription = user?.subscription;
  const isTrialExpired =
    !!subscription &&
    subscription.status === 'trialing' &&
    !!subscription.currentPeriodEnd &&
    new Date(subscription.currentPeriodEnd) < new Date();

  // Focus trap + Escape-to-logout. Effect only runs while the modal is open
  // so we don't pollute global key handlers when the user is in good standing.
  useEffect(() => {
    if (!isTrialExpired) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = containerRef.current;
    if (!node) return;

    // Move focus into the dialog on mount
    const focusables = node.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusables[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Escape escapes via logout, not by closing the dialog.
        e.preventDefault();
        void logout();
        return;
      }
      if (e.key !== 'Tab' || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Lock body scroll while the dialog is up
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isTrialExpired, logout]);

  if (!isTrialExpired) return null;

  const handleSubscribe = () => {
    navigate('/pricing');
  };

  const handleLogout = () => {
    void logout();
  };

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="trial-expired-title"
      aria-describedby="trial-expired-desc"
      className="fixed inset-0 z-[10000] flex items-center justify-center"
    >
      {/* Non-dismissible backdrop — no onClick handler. */}
      <div
        data-testid="trial-expired-backdrop"
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      <div
        ref={containerRef}
        className="relative w-[28rem] max-w-[92vw] bg-bg-secondary border border-border-default rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Accent stripe matches the golden-bull theme tokens */}
        <div className="h-1 bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-primary" />

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-accent-primary/15 flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-accent-primary" aria-hidden="true" />
            </div>
            <h2
              id="trial-expired-title"
              className="text-xl font-semibold text-text-primary"
            >
              {t('trial.expiredModal.title')}
            </h2>
          </div>

          <p
            id="trial-expired-desc"
            className="text-sm text-text-secondary leading-relaxed mb-6"
          >
            {t('trial.expiredModal.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="primary"
              onClick={handleSubscribe}
              className="flex-1 gap-2"
              autoFocus
            >
              {t('trial.expiredModal.subscribe')}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Button>
            <Button
              variant="secondary"
              onClick={handleLogout}
              className="flex-1 gap-2"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              {t('trial.expiredModal.logout')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
